import { useTranslation } from 'react-i18next';
import type { DaySummary } from '@kraftwerk/shared';

/**
 * Der Tagesstand: Protein, Kalorien, Kohlenhydrate, Fett in **einer** Kachel.
 *
 * Bis 18.10.2026 stand Protein riesig in einer eigenen Kachel und die drei
 * übrigen Werte in je einer darunter — gut ein Drittel des Bildschirms, bevor
 * die erste Mahlzeit kam. Protein steht weiter vorn, weil es die Zahl ist,
 * die sich am Tag noch beeinflussen lässt; die übrigen drei stehen
 * vollständig daneben, weggelassen würde die Anzeige zu einer Behauptung
 * darüber, was zählt.
 *
 * Die Balken werden **nicht gedeckelt**: Wer über dem Ziel liegt, sieht das an
 * der Farbe. Ein Balken, der bei 100 % stehen bleibt, verschweigt genau den
 * Fall, für den man hinschaut — die Zahl darunter sagt weiterhin die Wahrheit.
 */
export function MacroBars({
  summary,
  children,
}: {
  summary: DaySummary;
  /** Ballaststoffe, Zucker, Salz — eingeklappt in derselben Kachel darunter. */
  children?: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { consumed, targets, ratio } = summary.progress;

  const macros = [
    {
      key: 'protein',
      value: consumed.protein,
      target: targets.protein,
      ratio: ratio.protein,
      unit: 'g',
    },
    { key: 'carbs', value: consumed.carbs, target: targets.carbs, ratio: ratio.carbs, unit: 'g' },
    { key: 'fat', value: consumed.fat, target: targets.fat, ratio: ratio.fat, unit: 'g' },
    // kcal rechts außen (18.10.2026): die Summe der drei, nicht ihr Nachbar.
    { key: 'kcal', value: consumed.kcal, target: targets.kcal, ratio: ratio.kcal, unit: 'kcal' },
  ] as const;

  return (
    <section aria-labelledby="macros-heading" className="kw-tile flex flex-col gap-3">
      <h3 id="macros-heading" className="sr-only">
        {t('nutrition.day.macrosHeading')}
      </h3>
      <ul className="grid grid-cols-4 gap-3">
        {macros.map((macro) => (
          <li key={macro.key} className="min-w-0">
            {/* Kurzname sichtbar, voller Name für Vorleser: „Kohlenhydrate“
                passt auf 375 px in kein Viertel. */}
            <p className="truncate text-xs font-medium text-ink-muted">
              <span aria-hidden="true">{t(`nutrition.macroShort.${macro.key}`)}</span>
              <span className="sr-only">{t(`nutrition.macro.${macro.key}`)}</span>
            </p>
            <p
              className={`kw-figure leading-tight ${macro.key === 'protein' ? 'text-2xl' : 'text-xl'}`}
            >
              {Math.round(macro.value)}
            </p>
            <p className="text-xs text-ink-muted">
              / {Math.round(macro.target)} {macro.unit}
            </p>
            <Bar ratio={macro.ratio} />
          </li>
        ))}
      </ul>

      {/* Bis 20.10.2026 stand hier noch „Noch … g Protein · noch … kcal ·
          … Portionen mit mindestens 30 g“ — dieselben Zahlen ein zweites Mal,
          nur in Worten. Die Kachel sagt es mit Zahl und Balken. */}
      {children}
    </section>
  );
}

function Bar({ ratio }: { ratio: number }): React.JSX.Element {
  const over = ratio > 1;
  const share = Math.min(1, Math.max(0, ratio)) * 100;

  return (
    <span className="kw-bar kw-bar-thin mt-1.5" aria-hidden="true">
      <i className={over ? 'bg-warn' : ''} style={{ width: `${share}%` }} />
    </span>
  );
}
